import React from "react";
import dayjs from "dayjs";
import { Typography } from "@material-ui/core";
import { ButtonWrap, Icon, IconName } from "halifax";
import { RecentExperienceSearch } from "redmond";

import {
  CombinedSearchGroup,
  CombinedSearchGroupType,
  CombinedSearchSuggestion,
  MAX_RESULTS_PER_GROUP,
  TripDestination,
} from "../../types";
import { TripDestinationsSection } from "../TripDestinationsSection";
import {
  DestinationIcon,
  CategoriesIcon,
  ProductsIcon,
  CollectionsIcon,
} from "./icons";

import "./styles.scss";

function formatRecentSearchLabel(search: RecentExperienceSearch): string {
  if (
    search.suggestionType &&
    search.suggestionType !== "Destination" &&
    search.location
  ) {
    try {
      const cache = JSON.parse(
        localStorage.getItem("dest_name_cache") || "{}",
      );
      const destName = cache[search.location];
      if (destName) {
        return `${search.locationLabel} \u00B7 ${destName}`;
      }
    } catch {
      // Ignore
    }
  }
  return search.locationLabel;
}

function formatRecentSearchSubLabel(search: RecentExperienceSearch): string {
  const parts: string[] = [];

  if (search.activityCount && search.activityCount > 0) {
    parts.push(
      `${search.activityCount.toLocaleString()} ${search.activityCount === 1 ? "activity" : "activities"}`,
    );
  }

  if (search.fromDate) {
    const from = dayjs(search.fromDate);
    if (search.untilDate && search.untilDate !== search.fromDate) {
      const until = dayjs(search.untilDate);
      parts.push(`${from.format("MMM D")} - ${until.format("MMM D")}`);
    } else {
      parts.push(from.format("MMM D"));
    }
  }

  return parts.join(" \u00B7 ");
}

export interface CombinedSearchDropdownProps {
  groups: CombinedSearchGroup[];
  loading: boolean;
  hasError: boolean;
  query: string;
  recentSearches: RecentExperienceSearch[];
  tripDestinations: TripDestination[];
  showTrips: boolean;
  tripsLoading: boolean;
  imageVariant: "images" | "icons" | "both" | "control";
  onResultClick: (
    suggestion: CombinedSearchSuggestion,
    groupType: CombinedSearchGroupType,
    index: number
  ) => void;
  onRecentSearchClick: (search: RecentExperienceSearch, index: number) => void;
  onTripClick: (trip: TripDestination, index: number) => void;
  hasSearched?: boolean;
}

const GROUP_HEADING_MAP: Record<string, string> = {
  Destination: "Destinations",
  Attraction: "Categories",
  Category: "Categories",
  Product: "Products",
  Collection: "Collections",
};

const GROUP_ICON_COMPONENT: Record<string, React.FC<{ className?: string }>> = {
  Destination: DestinationIcon,
  Category: CategoriesIcon,
  Attraction: CategoriesIcon,
  Product: ProductsIcon,
  Collection: CollectionsIcon,
};

const SuggestionRow = ({
  suggestion,
  groupType,
  imageVariant,
  onClick,
}: {
  suggestion: CombinedSearchSuggestion;
  groupType: string;
  imageVariant: string;
  onClick: () => void;
}) => {
  const showImage =
    imageVariant === "images" || imageVariant === "both";
  const IconComponent = GROUP_ICON_COMPONENT[groupType] ?? DestinationIcon;

  const fullLabel = [
    suggestion.label,
    suggestion.activityCount
      ? `${suggestion.activityCount} activities`
      : null,
    suggestion.destinationName,
  ]
    .filter(Boolean)
    .join(" • ");

  return (
    <ButtonWrap
      className="combined-search-suggestion-item"
      onClick={onClick}
      tabIndex={-1}
      title={fullLabel}
    >
      <div className="suggestion-icon-container">
        {showImage && suggestion.thumbnailUrl ? (
          <img
            className="suggestion-thumbnail"
            src={suggestion.thumbnailUrl}
            alt=""
            loading="lazy"
          />
        ) : (
          <IconComponent className="suggestion-type-icon" />
        )}
      </div>
      <div className="suggestion-details">
        <Typography className="suggestion-label">
          {suggestion.label}
        </Typography>
        {(groupType === "Attraction" || groupType === "Category") && (() => {
          const parts = [
            suggestion.activityCount != null && suggestion.activityCount > 0
              ? `${suggestion.activityCount.toLocaleString()} ${suggestion.activityCount === 1 ? "activity" : "activities"}`
              : null,
            suggestion.destinationName,
          ].filter(Boolean);
          return parts.length > 0 ? (
            <Typography className="suggestion-sub-label">
              {parts.join(" \u00B7 ")}
            </Typography>
          ) : null;
        })()}
        {groupType === "Product" && (
          <Typography className="suggestion-sub-label">
            {[
              suggestion.reviewAverage != null
                ? `${suggestion.reviewAverage.toFixed(1)}★`
                : null,
              suggestion.destinationName,
            ]
              .filter(Boolean)
              .join(" \u00B7 ")}
          </Typography>
        )}
        {groupType === "Destination" && suggestion.subLabel && (
          <Typography className="suggestion-sub-label">
            {suggestion.subLabel}
          </Typography>
        )}
      </div>
    </ButtonWrap>
  );
};

export const CombinedSearchDropdown = ({
  groups,
  loading,
  hasError,
  query,
  recentSearches,
  tripDestinations,
  showTrips,
  tripsLoading,
  imageVariant,
  onResultClick,
  onRecentSearchClick,
  onTripClick,
  hasSearched = true,
}: CombinedSearchDropdownProps) => {
  const isEmptyQuery = query.trim().length === 0;

  // Filter recent searches + trips by query when user is typing
  const filteredRecentSearches = isEmptyQuery
    ? recentSearches
    : recentSearches.filter((s) =>
        s.locationLabel.toLowerCase().includes(query.toLowerCase())
      );

  const filteredTrips = isEmptyQuery
    ? tripDestinations
    : tripDestinations.filter((t) =>
        t.displayName.toLowerCase().includes(query.toLowerCase())
      );

  // Empty query: show recent searches + trip suggestions
  if (isEmptyQuery) {
    const showImages =
      imageVariant === "images" || imageVariant === "both";
    const hasContent =
      (showTrips && (tripsLoading || tripDestinations.length > 0)) ||
      recentSearches.length > 0;

    if (!hasContent) return null;

    return (
      <div className="combined-search-dropdown">
        {recentSearches.length > 0 && (
          <div className="combined-search-group">
            <Typography className="combined-search-group-heading">
              Recently searched
            </Typography>
            {recentSearches.map((search, i) => {
              const subLabel = formatRecentSearchSubLabel(search);
              return (
              <ButtonWrap
                key={`recent-${i}`}
                className="combined-search-suggestion-item"
                onClick={() => onRecentSearchClick(search, i)}
                tabIndex={-1}
              >
                <div className="suggestion-icon-container">
                  {search.thumbnailUrl && showImages ? (
                    <img
                      src={search.thumbnailUrl}
                      alt=""
                      className="suggestion-thumbnail"
                    />
                  ) : (
                    <Icon name={IconName.ClockIcon} ariaLabel="" aria-hidden />
                  )}
                </div>
                <div className="suggestion-details">
                  <Typography className="suggestion-label">
                    {formatRecentSearchLabel(search)}
                  </Typography>
                  {subLabel && (
                    <Typography className="suggestion-sub-label">
                      {subLabel}
                    </Typography>
                  )}
                </div>
              </ButtonWrap>
              );
            })}
          </div>
        )}
        {showTrips && (tripDestinations.length > 0 || tripsLoading) && (
          <TripDestinationsSection
            tripDestinations={tripDestinations}
            loading={tripsLoading}
            onTripClick={onTripClick}
            recentSearches={[]}
            onRecentSearchClick={() => {}}
          />
        )}
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="combined-search-dropdown">
        <div className="combined-search-loading">
          <Typography className="combined-search-loading-text">
            Searching...
          </Typography>
        </div>
      </div>
    );
  }

  // Error state
  if (hasError) {
    return (
      <div className="combined-search-dropdown">
        <div className="combined-search-error">
          <Typography className="combined-search-error-text">
            We're having trouble searching right now. Please try again.
          </Typography>
        </div>
      </div>
    );
  }

  const hasFilteredRecent = filteredRecentSearches.length > 0;
  const hasFilteredTrips = showTrips && filteredTrips.length > 0;

  // When recent searches match the query, hide Destination results to avoid redundancy.
  // Also deduplicate suggestions by label within each group (e.g., multiple "Museums" categories).
  const displayGroups = groups
    .filter((g) => !(hasFilteredRecent && g.groupType === "Destination"))
    .map((g) => {
      const seenLabels = new Set<string>();
      return {
        ...g,
        suggestions: g.suggestions.filter((s) => {
          const key = s.label.toLowerCase();
          if (seenLabels.has(key)) return false;
          seenLabels.add(key);
          return true;
        }),
      };
    })
    .filter((g) => g.suggestions.length > 0);

  const hasApiResults = displayGroups.some((g) => g.suggestions.length > 0);

  // No results state
  if (!hasApiResults && !hasFilteredRecent && !hasFilteredTrips) {
    // Don't show "No results" if no search has been triggered yet
    if (!hasSearched) return null;
    return (
      <div className="combined-search-dropdown">
        <div className="combined-search-group">
          <Typography className="combined-search-group-heading">
            Results
          </Typography>
          <Typography className="combined-search-no-results-text">
            No results found for &ldquo;{query}&rdquo;
          </Typography>
        </div>
      </div>
    );
  }

  // Results state: filtered recent/trips + grouped API results
  let flatIndex = 0;

  return (
    <div className="combined-search-dropdown">
      {/* Filtered recent searches while typing */}
      {hasFilteredRecent && (
        <div className="combined-search-group">
          <Typography className="combined-search-group-heading">
            Recent searches
          </Typography>
          {filteredRecentSearches.map((search, i) => {
            const subLabel = formatRecentSearchSubLabel(search);
            return (
            <ButtonWrap
              key={`recent-${i}`}
              className="combined-search-suggestion-item"
              onClick={() => onRecentSearchClick(search, i)}
              tabIndex={-1}
            >
              <div className="suggestion-icon-container">
                {search.thumbnailUrl &&
                (imageVariant === "images" || imageVariant === "both") ? (
                  <img
                    src={search.thumbnailUrl}
                    alt=""
                    className="suggestion-thumbnail"
                  />
                ) : (
                  <Icon name={IconName.ClockIcon} ariaLabel="" aria-hidden />
                )}
              </div>
              <div className="suggestion-details">
                <Typography className="suggestion-label">
                  {formatRecentSearchLabel(search)}
                </Typography>
                {subLabel && (
                  <Typography className="suggestion-sub-label">
                    {subLabel}
                  </Typography>
                )}
              </div>
            </ButtonWrap>
            );
          })}
        </div>
      )}

      {/* Filtered trip suggestions while typing */}
      {hasFilteredTrips && (
        <div className="combined-search-group">
          <Typography className="combined-search-group-heading">
            Your upcoming trips
          </Typography>
          {filteredTrips.map((trip, i) => (
            <ButtonWrap
              key={`trip-${i}`}
              className="combined-search-suggestion-item"
              onClick={() => onTripClick(trip, i)}
              tabIndex={-1}
            >
              <div className="suggestion-icon-container">
                <Icon
                  name={
                    trip.source === "Flight"
                      ? IconName.TripFlightIcon
                      : IconName.TripHotelIcon
                  }
                  ariaLabel=""
                  aria-hidden
                />
              </div>
              <div className="suggestion-details">
                <Typography className="suggestion-label">
                  {trip.displayName}
                </Typography>
              </div>
            </ButtonWrap>
          ))}
        </div>
      )}

      {/* API grouped results — merge groups that share the same heading */}
      {(() => {
        // Merge groups by display heading (e.g., Attraction + Category both → "Categories")
        const mergedByHeading: { heading: string; items: { suggestion: CombinedSearchSuggestion; groupType: CombinedSearchGroupType }[] }[] = [];
        for (const group of displayGroups) {
          if (group.suggestions.length === 0) continue;
          const heading = GROUP_HEADING_MAP[group.groupType] || group.groupType;
          const existing = mergedByHeading.find((m) => m.heading === heading);
          const items = group.suggestions.map((s) => ({ suggestion: s, groupType: group.groupType }));
          if (existing) {
            existing.items.push(...items);
          } else {
            mergedByHeading.push({ heading, items });
          }
        }
        // Apply max results per merged heading section
        for (const section of mergedByHeading) {
          section.items = section.items.slice(0, MAX_RESULTS_PER_GROUP);
        }

        return mergedByHeading.map(({ heading, items }) => (
          <div className="combined-search-group" key={heading}>
            <Typography className="combined-search-group-heading">
              {heading}
            </Typography>
            {items.map(({ suggestion, groupType }) => {
              const currentIndex = flatIndex++;
              return (
                <SuggestionRow
                  key={`${groupType}-${suggestion.id}`}
                  suggestion={suggestion}
                  groupType={groupType}
                  imageVariant={imageVariant}
                  onClick={() =>
                    onResultClick(suggestion, groupType, currentIndex)
                  }
                />
              );
            })}
          </div>
        ));
      })()}
    </div>
  );
};
